import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalAuth } from '../contexts/HospitalAuthContext';
import { apiRequest } from '../config/api';
import TrialExpiredPage from '../pages/hospitals/TrialExpiredPage';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

const HospitalRouteGuard = ({ children }) => {
  const { hospital, subscriptionStatus, loading, refreshHospitalStatus } = useHospitalAuth();
  const [checking, setChecking] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      setChecking(true);
      
      // Check if hospital data exists
      const storedHospital = localStorage.getItem('hospital_data');
      if (!storedHospital) {
        // No hospital session, redirect to hospital login
        navigate('/hospital/login');
        setChecking(false);
        return;
      }

      try {
        const hospitalData = JSON.parse(storedHospital);
        
        // Get current hospital status
        const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${hospitalData.id}`);
        
        if (statusData.success) {
          // Check if hospital is approved
          if (statusData.hospital.status !== 'APPROVED') {
            setAccessGranted(false);
            setChecking(false);
            return;
          }
          
          // Check if subscription/trial is active
          if (!statusData.subscription.active) {
            setAccessGranted(false);
            setChecking(false);
            return;
          }
          
          // Access granted
          setAccessGranted(true);
        } else {
          setAccessGranted(false);
        }
      } catch (error) {
        console.error('Access check error:', error);
        setAccessGranted(false);
      } finally {
        setChecking(false);
      }
    };

    checkAccess();
  }, [navigate, refreshHospitalStatus]);

  if (checking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <RefreshCw className="h-8 w-8 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-300">Checking access...</p>
        </motion.div>
      </div>
    );
  }

  if (!accessGranted) {
    // Show trial/expired page
    return <TrialExpiredPage />;
  }

  return <>{children}</>;
};

export default HospitalRouteGuard;

